import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './HeroFrameMachine.css';
import PreparedStickerCard from './PreparedStickerCard';
import ClothedEyeLoader from './ClothedEyeLoader';
import MaterialIcon from './MaterialIcon';
import randomStickerService from '../services/randomStickerService';
import FramePositioningAdmin from './FramePositioningAdmin';

import logger from '../utils/logger.js';
const HeroFrameMachine = ({ onStickerClick = () => {} }) => {
  const [centerSticker, setCenterSticker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const piecesContainerRef = useRef(null);
  const centerStickerRef = useRef(null);

  // Simple animation state management
  const [isIrisOpen, setIsIrisOpen] = useState(false);
  const [stickerDimensions, setStickerDimensions] = useState({ width: 120, height: 120 });

  // Auto-open animation state management
  const [autoOpenPending, setAutoOpenPending] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  // Frame positioning state - all relative to base piece
  const [framePositions, setFramePositions] = useState({
    'upper-left-corner': { x: 120, y: 120, zIndex: 11 }, // Base piece
    'upper-right-corner': { x: 90, y: 0, zIndex: 11 },
    'top-bar': { x: -90, y: -10, zIndex: 9 },
    'left-side': { x: 10, y: -170, zIndex: 9 },
    'right-bottom-edge': { x: -160, y: -170, zIndex: 5 },
    'frame-bottom-edge': { x: -800, y: -140, zIndex: 3 }, // New piece - positioned left of viewport, aligned with bottom of right-bottom-edge
    'frame-top-right-edge': { x: -800, y: -280, zIndex: 3 }, // New piece - attached to upper-right corner, fixed position
    'screw-top': { x: 45, y: -7, zIndex: 10 },
    'screw-left': { x: -18, y: 69, zIndex: 10 },
    'screw-right': { x: 111, y: 89, zIndex: 10 },
    'screw-bottom': { x: 60, y: 143, zIndex: 10 },
    'center-sticker': { x: 21, y: 24, zIndex: 4 }, // Offset from upper-left-corner piece to account for piece dimensions
  });

  // Dimension detection and calculation utilities
  const calculateStickerDimensions = useCallback(() => {
    if (!centerStickerRef.current) {
      logger.warn('centerStickerRef not found');
      return { width: 120, height: 120 };
    }

    const preparedStickerCard = centerStickerRef.current.querySelector('.prepared-card');
    if (!preparedStickerCard) {
      logger.warn('PreparedStickerCard (.prepared-card) not found');
      return { width: 120, height: 120 };
    }

    const rect = preparedStickerCard.getBoundingClientRect();
    logger.log('Successfully measured PreparedStickerCard element:', {
      element: preparedStickerCard,
      className: preparedStickerCard.className,
      boundingRect: {
        width: rect.width,
        height: rect.height,
        x: rect.x,
        y: rect.y,
      },
    });

    return {
      width: rect.width,
      height: rect.height,
    };
  }, []);

  // Dynamic iris positioning based on current sticker dimensions
  const getIrisOpenPositions = useCallback(() => {
    // Simple approach: move pieces by FULL sticker dimensions + small consistent gap
    const gapH = -30; // Small consistent visual gap around sticker
    const gapV = -80;

    const horizontalClearance = stickerDimensions.width + gapH;
    const verticalClearance = stickerDimensions.height + gapV;

    logger.log(`Direct clearance for ${stickerDimensions.width}×${stickerDimensions.height}:`, {
      horizontalClearance,
      verticalClearance,
      formula: 'width + 20px gap, height + 20px gap',
    });

    // Dynamic movement based on actual sticker size - back to working multipliers
    const openMovements = {
      // Upper-left stays fixed (true anchor point)
      'upper-left-corner': { x: 0, y: 0 },

      // Upper-right moves horizontally to provide full clearance + frame piece width
      'upper-right-corner': { x: horizontalClearance, y: 0 },

      // Left side pieces move DOWN to provide full vertical clearance
      'left-side': { x: 0, y: verticalClearance },
      'screw-left': { x: 0, y: verticalClearance * 0.8 },

      // Top pieces move RIGHT to provide full horizontal clearance
      'top-bar': { x: horizontalClearance, y: 0 },
      'screw-top': { x: horizontalClearance * 0.8, y: 0 },

      // Right/bottom pieces move both directions to provide full clearance
      'right-bottom-edge': { x: horizontalClearance, y: verticalClearance },

      // Frame-bottom-edge moves ONLY vertically (same Y movement as right-bottom-edge)
      'frame-bottom-edge': { x: 0, y: verticalClearance },

      // Frame-top-right-edge stays FIXED (no movement like upper-left-corner)
      'frame-top-right-edge': { x: 0, y: 0 },

      'screw-right': { x: horizontalClearance * 0.9, y: verticalClearance * 0.8 },
      'screw-bottom': { x: horizontalClearance * 0.7, y: verticalClearance * 0.9 },

      // Center sticker does NOT move - it stays fixed in position
    };

    return openMovements;
  }, [stickerDimensions]);

  // Dynamic iris animation functions
  const toggleIris = useCallback(() => {
    if (!piecesContainerRef.current) return;

    const newState = !isIrisOpen;
    setIsIrisOpen(newState);

    if (newState) {
      // Opening iris - positions are already set by useEffect
      piecesContainerRef.current.classList.add('iris-open');
    } else {
      // Closing iris - remove transforms
      piecesContainerRef.current.classList.remove('iris-open');
    }
  }, [isIrisOpen]);

  const openIris = useCallback(() => {
    if (!isIrisOpen) {
      toggleIris();
    }
  }, [isIrisOpen, toggleIris]);

  const closeIris = useCallback(() => {
    if (isIrisOpen) {
      toggleIris();
    }
  }, [isIrisOpen, toggleIris]);

  const recycleSticker = useCallback(async () => {
    try {
      // Close iris first
      closeIris();

      // Wait for iris close animation to complete
      setTimeout(async () => {
        // Setup for auto-open after new sticker loads
        setAutoOpenPending(true);
        setHasAutoOpened(false);
        setLoading(true);

        try {
          // Get new sticker (isolated to this component)
          const newSticker = await randomStickerService.getRandomSticker();
          setCenterSticker(newSticker);
          // Dimensions will be updated via onLoadComplete callback when the new sticker loads
        } catch (error) {
          console.error('Failed to recycle sticker:', error);
        } finally {
          setLoading(false);
        }
      }, 800); // Wait for iris close animation (--iris-duration)
    } catch (error) {
      console.error('Failed to start recycle sequence:', error);
    }
  }, [closeIris]);

  // Handle when sticker card is fully loaded and rendered
  const handleStickerLoadComplete = useCallback(() => {
    logger.log('Sticker load complete, measuring dimensions...');

    // Use requestAnimationFrame to ensure DOM has fully rendered
    requestAnimationFrame(() => {
      const newDimensions = calculateStickerDimensions();
      logger.log('Measured dimensions:', newDimensions);
      setStickerDimensions(newDimensions);

      // Auto-open iris if pending
      if (autoOpenPending && !hasAutoOpened && !loading) {
        setTimeout(() => {
          openIris();
          setHasAutoOpened(true);
          setAutoOpenPending(false);
        }, 500);
      }
    });
  }, [calculateStickerDimensions, autoOpenPending, hasAutoOpened, loading, openIris]);

  // Update CSS custom properties when positions change
  useEffect(() => {
    if (piecesContainerRef.current) {
      Object.entries(framePositions).forEach(([pieceName, pos]) => {
        if (pieceName === 'upper-left-corner') {
          // Base piece controls the base position
          piecesContainerRef.current.style.setProperty(`--base-x`, `${pos.x}px`);
          piecesContainerRef.current.style.setProperty(`--base-y`, `${pos.y}px`);
        } else if (pieceName === 'frame-bottom-edge' || pieceName === 'frame-top-right-edge') {
          // Fixed-position elements - skip CSS variable setting entirely, they use fixed viewport positioning
          // Do nothing - these elements use hardcoded CSS positioning
        } else {
          // Other pieces are positioned relative to base
          piecesContainerRef.current.style.setProperty(`--${pieceName}-x`, `${pos.x}px`);
          piecesContainerRef.current.style.setProperty(`--${pieceName}-y`, `${pos.y}px`);
        }
        piecesContainerRef.current.style.setProperty(`--${pieceName}-z`, pos.zIndex);
      });
    }
  }, [framePositions]);

  // Dimension measurement now handled by onLoadComplete callback from PreparedStickerCard

  // Update iris positions whenever sticker dimensions change
  useEffect(() => {
    if (piecesContainerRef.current && stickerDimensions) {
      const openPositions = getIrisOpenPositions();
      Object.entries(openPositions).forEach(([pieceName, pos]) => {
        piecesContainerRef.current.style.setProperty(`--${pieceName}-open-x`, `${pos.x}px`);
        piecesContainerRef.current.style.setProperty(`--${pieceName}-open-y`, `${pos.y}px`);
      });

      // Set backing box dimensions based on sticker size with 10px padding
      piecesContainerRef.current.style.setProperty(
        `--backing-box-width`,
        `${stickerDimensions.width + 10}px`
      );
      piecesContainerRef.current.style.setProperty(
        `--backing-box-height`,
        `${stickerDimensions.height + 10}px`
      );

      logger.log(
        'Updated iris positions for sticker dimensions:',
        stickerDimensions,
        openPositions
      );
    }
  }, [stickerDimensions, getIrisOpenPositions]);

  // Load a single random sticker on component mount
  useEffect(() => {
    const loadSticker = async () => {
      // Start with iris closed and prepare for auto-open
      setIsIrisOpen(false);
      setAutoOpenPending(true);
      setHasAutoOpened(false);
      setLoading(true);

      try {
        // Get next sticker in global variety sequence
        const sticker = await randomStickerService.getRandomSticker();
        setCenterSticker(sticker);
        // Sticker loaded, ready for positioning
      } catch (error) {
        console.error('Failed to load random sticker:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSticker();
  }, []); // Empty dependency array - only run once

  // Handle refresh - select new random sticker
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const sticker = await randomStickerService.getRandomSticker();
      setCenterSticker(sticker);
    } catch (error) {
      console.error('Failed to load random sticker:', error);
    } finally {
      setLoading(false);
    }
  };

  // Admin overlay handlers
  const handlePositionChange = (newPositions) => {
    setFramePositions(newPositions);
  };

  const handleSelectPiece = (pieceName) => {
    setSelectedPiece(pieceName);
  };

  const handleSave = (positions) => {
    // Generate complete CSS file content
    const completeCSS = generateCompleteCSS(positions);

    logger.log('=== COMPLETE HEROFRAMEMACHINE.CSS FILE ===');
    logger.log(completeCSS);
    logger.log('=== COPY ABOVE ENTIRE CSS FILE TO HEROFRAMEMACHINE.CSS ===');

    // Also copy to clipboard if possible
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(completeCSS)
        .then(() => {
          alert('Complete CSS file copied to clipboard!');
        })
        .catch(() => {
          alert('Complete CSS file generated! Check console to copy.');
        });
    } else {
      alert('Complete CSS file generated! Check console to copy.');
    }

    // Update the current state
    setFramePositions(positions);
  };

  const generateCompleteCSS = (positions) => {
    // Base CSS content with all the existing styles
    let css = `/* Hero Frame Machine Styles */
.hero-frame-machine {
  position: relative;
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a1d23 0%, #2c3e50 100%);
  overflow: hidden;
}

.hero-frame-machine-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-frame-assembly {
  position: relative;
  width: 400px;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-frame-pieces {
  position: relative;
  width: 100%;
  height: 100%;
  --base-x: 50%;
  --base-y: 50%;
}

.hero-frame-center-sticker {
  position: absolute;
  pointer-events: auto;
  transform: translate(-50%, -50%);
}

.hero-frame-center-sticker .prepared-sticker-card {
  transform-origin: center center;
}

/* Frame piece selection highlighting */
.hero-frame-piece.selected {
  outline: 3px solid #3498db;
  outline-offset: 2px;
  box-shadow: 0 0 10px rgba(52, 152, 219, 0.5);
}

/* Frame piece base styles */
.hero-frame-piece {
  position: absolute;
  transform: translate(-50%, -50%);
  pointer-events: auto;
  cursor: pointer;
  transition: all 0.2s ease;
}

.hero-frame-piece:hover {
  transform: translate(-50%, -50%) scale(1.05);
}

/* Generated Frame Positioning CSS */
`;

    // Add the positioning for each piece using CSS custom properties
    Object.entries(positions).forEach(([pieceName, pos]) => {
      const className =
        pieceName === 'frame-bottom-edge' ? `hero-frame-${pieceName}` : `hero-frame-${pieceName}`;
      css += `.${className} {\n`;
      if (pieceName === 'upper-left-corner') {
        // Base piece uses base coordinates directly
        css += `  top: var(--base-y);\n`;
        css += `  left: var(--base-x);\n`;
      } else if (pieceName === 'frame-bottom-edge' || pieceName === 'frame-top-right-edge') {
        // Fixed-position elements - always anchored to left edge of viewport
        css += `  position: fixed !important;\n`;
        css += `  top: calc(50vh + ${pos.y}px);\n`;
        css += `  left: 0px !important;\n`;
        css += `  transform: translateY(-50%) !important;\n`;
      } else {
        // Other pieces use relative positioning
        css += `  top: calc(var(--base-y) + var(--${pieceName}-y, ${pos.y}px));\n`;
        css += `  left: calc(var(--base-x) + var(--${pieceName}-x, ${pos.x}px));\n`;
      }
      css += `  z-index: var(--${pieceName}-z, ${pos.zIndex});\n`;
      css += `}\n\n`;
    });

    // Add the center sticker positioning using CSS custom properties
    css += `.hero-frame-center-sticker {\n`;
    css += `  top: calc(var(--base-y) + var(--center-sticker-y, 50px));\n`;
    css += `  left: calc(var(--base-x) + var(--center-sticker-x, 0px));\n`;
    css += `  z-index: var(--center-sticker-z, 4);\n`;
    css += `  position: absolute;\n`;
    css += `  overflow: hidden;\n`;
    css += `  clip-path: inset(0 50% 50% 0);\n`;
    css += `  transition: clip-path var(--iris-duration) var(--iris-easing);\n`;
    css += `}\n\n`;

    // Add loading and refresh button styles
    css += `/* Loading and Controls */
.hero-frame-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
}

.hero-frame-refresh {
  position: absolute;
  top: 20px;
  right: 20px;
  background: #3498db;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.3);
  transition: all 0.2s ease;
  z-index: 100;
}

.hero-frame-refresh:hover {
  background: #2980b9;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(52, 152, 219, 0.4);
}

.hero-frame-refresh:active {
  transform: translateY(0);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-frame-assembly {
    width: 300px;
    height: 300px;
  }
  
  .hero-frame-center-sticker .prepared-sticker-card {
    width: 100px;
    height: 100px;
    transform: scale(1.1);
  }
  
  .hero-frame-refresh {
    top: 10px;
    right: 10px;
    padding: 10px 16px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .hero-frame-assembly {
    width: 250px;
    height: 250px;
  }
  
  .hero-frame-center-sticker .prepared-sticker-card {
    width: 80px;
    height: 80px;
    transform: scale(1);
  }
}`;

    return css;
  };

  return (
    <div className="hero-frame-machine">
      <div className="hero-frame-machine-container">
        {/* HERO FRAME MACHINE - Frame pieces assembly */}
        <div className="hero-frame-assembly">
          {/* Frame Pieces */}
          <div className="hero-frame-pieces" ref={piecesContainerRef}>
            {/* Upper Left Corner - Base Piece with VIEW Button */}
            <div
              className={`hero-frame-piece hero-frame-upper-left-corner ${selectedPiece === 'upper-left-corner' ? 'selected' : ''}`}
            >
              <img
                src="/assets/images/gears/frame-upper-left-corner.png"
                alt="Frame Upper Left Corner"
                onClick={() => handleSelectPiece('upper-left-corner')}
                className="frame-piece-image"
              />
              {/* VIEW Button attached to upper-left corner - hidden during loading */}
              {!loading && (
                <button
                  className="frame-action-button view-button upper-left-button"
                  onClick={() => {
                    if (onStickerClick && centerSticker) {
                      onStickerClick(centerSticker);
                    }
                  }}
                  disabled={loading}
                  title="View Sticker Details"
                >
                  <MaterialIcon icon="visibility" />
                </button>
              )}

              {/* Loading indicator in place of VIEW button */}
              {loading && (
                <div className="upper-left-button">
                  <ClothedEyeLoader size="56px" showTagline={false} showLogo={false} />
                </div>
              )}
            </div>

            {/* Upper Right Corner with RECYCLE Button */}
            <div
              className={`hero-frame-piece hero-frame-upper-right-corner ${selectedPiece === 'upper-right-corner' ? 'selected' : ''}`}
            >
              <img
                src="/assets/images/gears/frame-upper-right-corner.png"
                alt="Frame Upper Right Corner"
                onClick={() => handleSelectPiece('upper-right-corner')}
                className="frame-piece-image"
              />
              {/* RECYCLE Button attached to upper-right corner */}
              <button
                className="frame-action-button recycle-button upper-right-button"
                onClick={recycleSticker}
                disabled={loading}
                title="Get New Sticker"
              >
                <MaterialIcon icon="recycling" />
              </button>
            </div>

            {/* Top Bar */}
            <img
              src="/assets/images/gears/frame-top-bar.png"
              alt="Frame Top Bar"
              className={`hero-frame-piece hero-frame-top-bar ${selectedPiece === 'top-bar' ? 'selected' : ''}`}
              onClick={() => handleSelectPiece('top-bar')}
            />

            {/* Left Side */}
            <img
              src="/assets/images/gears/frame-left-side.png"
              alt="Frame Left Side"
              className={`hero-frame-piece hero-frame-left-side ${selectedPiece === 'left-side' ? 'selected' : ''}`}
              onClick={() => handleSelectPiece('left-side')}
            />

            {/* Right Bottom Edge */}
            <img
              src="/assets/images/gears/frame-right-bottom-edge.png"
              alt="Frame Right Bottom Edge"
              className={`hero-frame-piece hero-frame-right-bottom-edge ${selectedPiece === 'right-bottom-edge' ? 'selected' : ''}`}
              onClick={() => handleSelectPiece('right-bottom-edge')}
            />

            {/* Frame Bottom Edge - extends to left edge */}
            <img
              src="/assets/images/gears/frame-bottom-edge.png"
              alt="Frame Bottom Edge"
              className={`hero-frame-piece hero-frame-frame-bottom-edge ${selectedPiece === 'frame-bottom-edge' ? 'selected' : ''}`}
              onClick={() => handleSelectPiece('frame-bottom-edge')}
            />

            {/* Frame Top Right Edge - attached to upper-right corner, fixed */}
            <img
              src="/assets/images/gears/frame-bottom-edge.png"
              alt="Frame Top Right Edge"
              className={`hero-frame-piece hero-frame-frame-top-right-edge ${selectedPiece === 'frame-top-right-edge' ? 'selected' : ''}`}
              onClick={() => handleSelectPiece('frame-top-right-edge')}
            />

            {/* Screws */}
            <img
              src="/assets/images/gears/frame-screw-top.png"
              alt="Frame Screw Top"
              className={`hero-frame-piece hero-frame-screw-top ${selectedPiece === 'screw-top' ? 'selected' : ''}`}
              onClick={() => handleSelectPiece('screw-top')}
            />

            <img
              src="/assets/images/gears/frame-screw-left.png"
              alt="Frame Screw Left"
              className={`hero-frame-piece hero-frame-screw-left ${selectedPiece === 'screw-left' ? 'selected' : ''}`}
              onClick={() => handleSelectPiece('screw-left')}
            />

            <img
              src="/assets/images/gears/frame-screw-right.png"
              alt="Frame Screw Right"
              className={`hero-frame-piece hero-frame-screw-right ${selectedPiece === 'screw-right' ? 'selected' : ''}`}
              onClick={() => handleSelectPiece('screw-right')}
            />

            <img
              src="/assets/images/gears/frame-screw-bottom.png"
              alt="Frame Screw Bottom"
              className={`hero-frame-piece hero-frame-screw-bottom ${selectedPiece === 'screw-bottom' ? 'selected' : ''}`}
              onClick={() => handleSelectPiece('screw-bottom')}
            />

            {/* Dark gray backing box - scales with iris animation */}
            {!loading && centerSticker && <div className="hero-frame-backing-box"></div>}

            {/* Center Sticker - anchored to frame */}
            {!loading && centerSticker && (
              <div className="hero-frame-center-sticker" ref={centerStickerRef}>
                <PreparedStickerCard
                  id={centerSticker.id}
                  svgPath={centerSticker.svgPath}
                  title={centerSticker.title}
                  size={centerSticker.size || 'MEDIUM'}
                  colorIndex={centerSticker.colorIndex}
                  width={centerSticker.width}
                  height={centerSticker.height}
                  onStickerClick={onStickerClick}
                  showInteractionButtons={false}
                  stickerData={centerSticker}
                  onNewSticker={recycleSticker}
                  onLoadComplete={handleStickerLoadComplete}
                />
              </div>
            )}
          </div>
        </div>

        {/* Iris Animation Controls - Hidden since automation handles iris */}
        <div className="hero-frame-iris-controls" style={{ display: 'none' }}>
          <button
            type="button"
            className="iris-control-button"
            onClick={openIris}
            disabled={isIrisOpen}
            title="Open Iris"
          >
            <MaterialIcon icon="visibility" />
            <span>OPEN</span>
          </button>
          <button
            type="button"
            className="iris-control-button"
            onClick={closeIris}
            disabled={!isIrisOpen}
            title="Close Iris"
          >
            <MaterialIcon icon="visibility_off" />
            <span>CLOSE</span>
          </button>
          <button
            type="button"
            className="iris-control-button recycle-button"
            onClick={recycleSticker}
            title="Recycle Sticker"
          >
            <MaterialIcon icon="recycling" />
            <span>RECYCLE</span>
          </button>
        </div>
      </div>

      {/* Admin Overlay 
      <FramePositioningAdmin
        framePositions={framePositions}
        onPositionChange={handlePositionChange}
        onSave={handleSave}
        selectedPiece={selectedPiece}
        onSelectPiece={handleSelectPiece}
      />*/}
    </div>
  );
};

// PropTypes validation
HeroFrameMachine.propTypes = {
  onStickerClick: PropTypes.func,
};

export default HeroFrameMachine;
